/**
 * TeamChatJoin — PUBLIC invite-acceptance flow.
 * Validates token, shows coach + chat context, requires parental consent
 * checkbox, and either attaches an existing account or creates a fresh
 * `chat_member` account inline.
 */
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ShieldCheck, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TeamChatJoin() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState('');
  const [consent, setConsent] = useState(false);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/team-chat/invites/${token}/validate`);
        setInvite(res.data);
        setName(res.data.display_name || '');
      } catch (err) {
        setError(err?.response?.data?.detail || 'This invite is not valid.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const accept = async () => {
    if (!consent) {
      toast.error('Please tick the consent box to continue.');
      return;
    }
    if (!invite.existing_user && (!name.trim() || password.length < 8)) {
      toast.error('Please enter a name and a password of at least 8 characters.');
      return;
    }
    setSubmitting(true);
    try {
      const body = { parental_consent: true };
      if (!invite.existing_user) {
        body.name = name.trim();
        body.password = password;
      }
      const res = await axios.post(`${API}/team-chat/invites/${token}/accept`, body);
      toast.success('Welcome! You can now log in to see the chat.');
      // Send them to login with email prefilled
      navigate(`/login?email=${encodeURIComponent(invite.invite_email)}&joined=1&chat=${res.data.chat_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to accept invite.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-10 h-10 text-[#007a33] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-xl border border-gray-200 p-6 text-center" data-testid="invite-error">
          <AlertCircle className="w-10 h-10 mx-auto text-red-500 mb-2" />
          <h1 className="text-lg font-bold text-gray-900 mb-1">Invite unavailable</h1>
          <p className="text-sm text-gray-500">{error}</p>
          <Button onClick={() => navigate('/')} variant="outline" className="mt-4">Go home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-md mx-auto bg-white rounded-xl border border-gray-200 p-6 sm:p-8" data-testid="invite-accept-page">
        <div className="text-center mb-6">
          <CheckCircle2 className="w-12 h-12 mx-auto text-[#007a33] mb-2" />
          <h1 className="text-xl font-black text-gray-900">You&apos;re invited to a team chat</h1>
        </div>

        <dl className="space-y-2 text-sm mb-6 pb-6 border-b border-gray-100">
          <div className="flex justify-between gap-3">
            <dt className="text-gray-500">Chat</dt>
            <dd className="font-semibold text-gray-900 text-right">{invite.chat_title}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-gray-500">Coach</dt>
            <dd className="font-semibold text-gray-900 text-right">
              {invite.coach_name}{invite.coach_team ? ` · ${invite.coach_team}` : ''}
            </dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-gray-500">Season</dt>
            <dd className="font-semibold text-gray-900 text-right">{invite.season_name || '—'}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-gray-500">You</dt>
            <dd className="font-semibold text-gray-900 text-right truncate">{invite.invite_email}</dd>
          </div>
        </dl>

        {!invite.existing_user && (
          <div className="space-y-3 mb-4">
            <div>
              <Label>Your name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Sarah Ryan"
                data-testid="join-name-input"
              />
            </div>
            <div>
              <Label>Choose a password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                data-testid="join-password-input"
              />
            </div>
          </div>
        )}

        {invite.existing_user && (
          <div className="mb-4 p-3 rounded-lg bg-blue-50 border border-blue-200 text-xs text-blue-900">
            We already have an account for <strong>{invite.invite_email}</strong>. Accept the invite, then log in as usual.
          </div>
        )}

        <label className="flex items-start gap-2 mb-4 cursor-pointer" data-testid="consent-label">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="mt-0.5"
            data-testid="parental-consent-checkbox"
          />
          <span className="text-xs text-gray-700 leading-snug">
            I confirm I am the parent or legal guardian of the invited player,
            <strong> OR</strong> I am the invited player and I am aged 16 or over.
            I have read the{' '}
            <a href="/docs/team-chat-safeguarding-policy.html" target="_blank" rel="noreferrer" className="text-[#007a33] underline">
              safeguarding policy
            </a>.
          </span>
        </label>

        <Button
          onClick={accept}
          disabled={!consent || submitting}
          className="w-full bg-[#007a33] hover:bg-[#005c26] text-white"
          data-testid="accept-invite-btn"
        >
          {submitting ? (
            <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Joining…</>
          ) : (
            <><ShieldCheck className="w-4 h-4 mr-1.5" />Accept invite</>
          )}
        </Button>

        <p className="text-[11px] text-gray-400 text-center mt-4">
          Grassroots Coaching · safeguarding-first team chat
        </p>
      </div>
    </div>
  );
}
